import type { CrewJobPayBand, CrewJobPayUnit } from '../types/crewJobs';

export function parseCrewJobPayAmount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.round(value * 100) / 100;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.replace(/[$,]/g, ''));
    if (Number.isFinite(n) && n > 0) return Math.round(n * 100) / 100;
  }
  return null;
}

export function parseCrewJobPayUnit(value: unknown): CrewJobPayUnit | null {
  if (value === 'hour' || value === 'hourly') return 'hour';
  if (value === 'day' || value === 'daily' || value === 'dayRate') return 'day';
  return null;
}

export function normalizeCrewJobPayBand(input: {
  unit?: unknown;
  min?: unknown;
  max?: unknown;
}): CrewJobPayBand | null {
  const unit = parseCrewJobPayUnit(input.unit);
  let min = parseCrewJobPayAmount(input.min);
  let max = parseCrewJobPayAmount(input.max);
  if (!unit || (min == null && max == null)) return null;
  if (min == null) min = max;
  if (max == null) max = min;
  if (min != null && max != null && min > max) {
    const swap = min;
    min = max;
    max = swap;
  }
  return { unit, min: min as number, max: max as number };
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatCrewJobPayBand(
  band: Pick<CrewJobPayBand, 'unit' | 'min' | 'max'> | null | undefined,
): string | null {
  if (!band || !(band.min > 0)) return null;
  const unit = band.unit === 'day' ? 'day' : 'hour';
  const low = formatUsd(band.min);
  const high = formatUsd(band.max);
  if (band.max > band.min) return `${low}–${high} / ${unit}`;
  return `${low} / ${unit}`;
}

export function payBandFromJob(job: {
  payUnit?: unknown;
  payMin?: unknown;
  payMax?: unknown;
}): CrewJobPayBand | null {
  return normalizeCrewJobPayBand({
    unit: job.payUnit,
    min: job.payMin,
    max: job.payMax,
  });
}
